import React, { useMemo, useState } from 'react';
import { Badge, Button, Icon, Input, Text, Tooltip } from '@ui-library';
import { IconName } from '@ui-library/Icon/Icon.type';
import {
  EnvironmentCredential,
  EnvironmentCredentialDecision,
  EnvironmentCredentialsSummary,
} from '../../services/projectEnvironment';

interface CredentialsSectionProps {
  summary: EnvironmentCredentialsSummary;
  /** Raised on every edit. `dirty` is false once the form is back where it
   *  started, so a Save button can turn itself off again. */
  onChange: (decisions: EnvironmentCredentialDecision[], dirty: boolean) => void;
  disabled?: boolean;
  /** Deploy dialog: collapsed behind "Customise", because a confirmation
   *  should not open into a form. Details panel: rendered open, because
   *  seeing the values is the reason the section is there. */
  collapsible?: boolean;
  /** "Credentials" in the deploy dialog, "Environment variables" in the panel
   *  — the same records, named the way each screen talks about them. */
  title?: string;
}

/** Only the states worth a pill. `new` gets none: a credential the project
 *  gained is just a project value nobody has decided about yet, which is what
 *  an unmarked row already means. */
const STATE_META: Partial<
  Record<EnvironmentCredential['state'], { label: string; tone: 'warning' | 'neutral' }>
> = {
  changed: { label: 'Changed', tone: 'warning' },
  removed: { label: 'Removed', tone: 'neutral' },
};

/** What an empty field is telling you.
 *
 * Only `secure` rows are ever empty with a value behind them now, and the two
 * of them mean opposite things: following the project, the box is empty
 * because the project's secret is never sent to the client; holding its own,
 * the box is empty because *no* secret is ever sent and leaving it alone keeps
 * what is stored.
 *
 * There used to be a third string — "The project's value will be used" — for a
 * reverted plain-text row, which was empty only because the project's value
 * was not on the response to show. It is now (`project_value`), so that row
 * shows the number like every other one and the sentence is gone. */
const placeholderFor = (
  item: EnvironmentCredential,
  source: 'project' | 'manual'
): string => {
  const secure = item.value_type === 'secure';
  if (!secure) return 'Enter a value for this environment';
  if (!item.has_value) return 'Enter a value for this environment';
  return source === 'project'
    ? "••••••••  ·  the project's value will be used"
    : '••••••••  ·  leave empty to keep the current value';
};

type Sources = Record<string, 'project' | 'manual'>;

/** The value to send for a row, or `undefined` to keep what is stored.
 *
 * An empty box means opposite things either side of `secure`. A `secure` value
 * is never sent to the client, so its field *starts* empty — empty is the
 * resting state, not an edit, and sending `''` for it would wipe a secret the
 * user only looked at. A `plain_text` field starts with the real value, so
 * clearing it is a deliberate act and `''` is what they asked for; the revert
 * button beside the row is how they undo it instead.
 *
 * Module-level and exported because this is the rule a secret gets destroyed
 * by if it is wrong, and a rule like that should be assertable without
 * mounting a form. */
export const valueToSend = (
  item: EnvironmentCredential,
  manual: Record<string, string>
): string | undefined => {
  const typed = manual[item.key_name];
  if (typed === undefined) return undefined;
  if (item.value_type === 'secure' && typed === '') return undefined;
  return typed;
};

/** What the deploy — or the Save button — will actually send. */
export const decisionsFor = (
  items: EnvironmentCredential[],
  sources: Sources,
  manual: Record<string, string>
): EnvironmentCredentialDecision[] =>
  items.map((item) => {
    const source = sources[item.key_name] || 'project';
    const value = source === 'manual' ? valueToSend(item, manual) : undefined;
    return {
      key_name: item.key_name,
      source,
      ...(value !== undefined ? { value } : {}),
    };
  });

/** Is there anything to save?
 *
 * Measured against where the form loaded, not against "has anything been
 * touched": typing a value and deleting it again leaves nothing to send, and a
 * Save button still lit after that is lying. Uses `valueToSend` for exactly
 * that reason — an emptied secure box is not a change. */
export const isDirty = (
  items: EnvironmentCredential[],
  sources: Sources,
  manual: Record<string, string>
): boolean =>
  items.some((i) => (sources[i.key_name] || 'project') !== i.source) ||
  items.some((i) => valueToSend(i, manual) !== undefined);

/**
 * The credentials an environment will deploy with, and what has moved.
 *
 * Two options only: a credential follows the project — resolved live at deploy,
 * so it can never be stale — or this environment sets its own. Nothing is
 * inherited from another environment.
 *
 * There used to be a radio pair per row for that choice. There isn't now:
 * typing in the field *is* choosing to set your own, and the revert button
 * beside it is choosing to go back. Two radios to express what the field
 * itself already expresses was a row of chrome per credential.
 *
 * Values are never shown for `secure` credentials — only whether one is set —
 * matching how the project's own credential list already behaves.
 */
export const CredentialsSection: React.FC<CredentialsSectionProps> = ({
  summary,
  onChange,
  disabled = false,
  collapsible = true,
  title = 'Credentials',
}) => {
  const items = useMemo(
    () => summary.items.filter((i) => i.state !== 'removed'),
    [summary.items]
  );
  const removed = useMemo(
    () => summary.items.filter((i) => i.state === 'removed'),
    [summary.items]
  );
  const changed = items.filter((i) => i.state === 'changed');

  const [customising, setCustomising] = useState(!collapsible);
  // key_name → typed value. Absent means "not retyped", which the backend reads
  // as "keep whatever is stored" rather than as a blank.
  const [manual, setManual] = useState<Record<string, string>>({});
  const [sources, setSources] = useState<Sources>(() =>
    Object.fromEntries(items.map((i) => [i.key_name, i.source]))
  );

  // Described from the decisions actually held, never assumed. On a first
  // deploy everything defaults to the project and this reads as it always did;
  // on a redeploy an environment may already have values of its own, and
  // claiming otherwise would be wrong about what is going to run.
  const ownCount = items.filter((i) => (sources[i.key_name] || 'project') === 'manual').length;
  const projectCount = items.length - ownCount;
  /** "1 credential" / "all 3 credentials" — "all" only when it really is all. */
  const count = (n: number, whole: boolean) =>
    n === 1 ? '1 credential' : `${whole ? 'all ' : ''}${n} credentials`;
  const valueWord = (n: number) => (n === 1 ? 'value' : 'values');
  const summaryLine =
    ownCount === 0
      ? `Using the project's ${valueWord(projectCount)} for ${count(projectCount, true)}.`
      : projectCount === 0
      ? `Using this environment's own ${valueWord(ownCount)} for ${count(ownCount, true)}.`
      : `Using this environment's own ${valueWord(ownCount)} for ${count(ownCount, false)}, ` +
        `and the project's for ${count(projectCount, false)}.`;

  const emit = (nextSources: Sources, nextManual: Record<string, string>) =>
    onChange(
      decisionsFor(items, nextSources, nextManual),
      isDirty(items, nextSources, nextManual)
    );

  /** Typing is the override — and emptying the box is typing.
   *
   * Clearing an inherited value used to hand the row back to the project,
   * which read as the field undoing the edit: a row showing the project's
   * `40`, backspaced to `4` and then to empty, re-rendered as `40`, because a
   * project row draws the project's value. Backspacing over a selection and
   * retyping then produced `405`, the cursor having landed after the text that
   * reappeared.
   *
   * So there is no special case any more. Clearing leaves an empty override,
   * marked `Own`, with the revert button beside it — which is the same thing
   * clearing an already-overridden row has always done, and `revert` below is
   * now the only way back to the project's value.
   *
   * An emptied `secure` box is still not an edit: it starts empty because the
   * value is never sent to the client, and `valueToSend` is where that is
   * decided. */
  const setValue = (item: EnvironmentCredential, value: string) => {
    const nextManual = { ...manual, [item.key_name]: value };
    const nextSources: Sources = { ...sources, [item.key_name]: 'manual' };
    setManual(nextManual);
    setSources(nextSources);
    emit(nextSources, nextManual);
  };

  /** Back to the project's value, this row only. */
  const revert = (key: string) => {
    const nextManual = { ...manual };
    delete nextManual[key];
    const nextSources = { ...sources, [key]: 'project' as const };
    setManual(nextManual);
    setSources(nextSources);
    emit(nextSources, nextManual);
  };

  const revertAll = () => {
    const reset = Object.fromEntries(items.map((i) => [i.key_name, 'project' as const]));
    setSources(reset);
    setManual({});
    emit(reset, {});
  };

  const rows = (
    // Capped rather than collapsed past some row count. A project with twenty
    // variables would otherwise push the panel's actions several screens down,
    // and hiding the list behind a "show all" defeats the reason it is here.
    <div className="flex flex-col gap-bg-v2-lg max-h-96 overflow-y-auto pr-bg-v2-xs">
      {items.map((item) => {
        const source = sources[item.key_name] || 'project';
        const meta = STATE_META[item.state];
        const secure = item.value_type === 'secure';
        // The response carries one value per credential — whichever one the
        // deploy would use — so the two a row can rest on have to be
        // disentangled before either can be displayed. `value` is the
        // environment's own exactly when the environment overrides; otherwise
        // it is already the project's, and `project_value` is absent for the
        // same reason.
        const ownValue = item.source === 'manual' ? item.value : null;
        const projectValue = item.source === 'manual' ? item.project_value : item.value;
        // Follows the *current* source, not the loaded one: revert a row and
        // the field shows what it reverted to rather than emptying and
        // describing it.
        const resting = source === 'manual' ? ownValue : projectValue;
        // A secret has no resting value to show — it never left the server.
        const value = manual[item.key_name] ?? (secure ? '' : resting ?? '');

        return (
          <div key={item.key_name} className="flex flex-col gap-bg-v2-xs w-full min-w-0">
            <div className="flex items-center gap-bg-v2-xs w-full min-w-0">
              {/* The key, never truncated — an env var name is the identifier,
                  and long shared prefixes (NEXT_PUBLIC_SUPABASE_URL vs
                  ..._ANON_KEY) clip to the same string. It wraps instead; the
                  field below it keeps the full width either way, which is the
                  whole reason the name is on its own line. */}
              <Text
                variant="text-xs"
                weight="medium"
                color="primary"
                className="font-mono break-all flex-1 min-w-0"
              >
                {item.key_name}
              </Text>
              {source === 'manual' && <Badge tone="brand">Own</Badge>}
              {meta && <Badge tone={meta.tone}>{meta.label}</Badge>}
              {item.description && (
                <Tooltip
                  // The popup is a light surface — `bg-bg-primary
                  // text-fg-primary` — so its content takes the ordinary text
                  // colours. `color="white"` here rendered white on white and
                  // the description was invisible rather than missing.
                  //
                  // `whitespace-normal` because the popup itself is
                  // `whitespace-nowrap`: every other caller passes a short
                  // string, and a description is a sentence that has to wrap
                  // or it runs off the screen.
                  content={
                    <div className="flex flex-col gap-bg-v2-xxs max-w-xs whitespace-normal break-words">
                      {/* <Text variant="text-xs" weight="medium" color="primary">
                        {item.display_name}
                      </Text> */}
                      <Text variant="text-xs" color="secondary">
                        {item.description}
                      </Text>
                    </div>
                  }
                  position="left"
                >
                  <span className="shrink-0 flex items-center text-text-tertiary cursor-help">
                    <Icon name={IconName.InfoCircle} size="xs" inheritColor />
                  </span>
                </Tooltip>
              )}
              {source === 'manual' && !disabled && (
                <button
                  type="button"
                  onClick={() => revert(item.key_name)}
                  title="Use the project's value for this one"
                  className="shrink-0 flex items-center justify-center w-[20px] h-[20px] rounded-full hover:bg-bg-secondary-hover transition-colors"
                >
                  <Icon
                    name={IconName.Revert}
                    size="xs"
                    className="text-text-tertiary"
                    inheritColor
                  />
                </button>
              )}
            </div>

            {/* A `plain_text` value is not a secret, so the field carries the
                value actually in use — editing it should not mean retyping
                from memory. A `secure` one is never sent to the client at all,
                so the field starts empty and says so; leaving it that way
                keeps whatever is stored. */}
            <Input
              id={`cred-${item.key_name}`}
              name={`cred-${item.key_name}`}
              type={secure ? 'password' : 'text'}
              size="sm"
              value={value}
              onChange={(e) => setValue(item, e.target.value)}
              placeholder={placeholderFor(item, source)}
              disabled={disabled}
            />
          </div>
        );
      })}
    </div>
  );

  return (
    <div className="flex flex-col gap-bg-v2-md pt-bg-v2-xl border-t border-border-secondary">
      <Text variant="text-xs" weight="medium" color="tertiary">
        {title}
      </Text>

      {/* Plain text, not a red block. Red in this dialog means destructive —
          losing data, or dropping migrations. A credential picking up a newer
          value destroys nothing, so it reads as information. The
          per-credential "Changed" badge is the visual marker, attached to the
          one that moved rather than shouting at the whole section. */}
      {changed.length > 0 && (
        <Text variant="text-xs" color="secondary" className="break-words">
          {changed.length === 1
            ? `'${changed[0].display_name}' changed in the project. Its new value will be used.`
            : `${changed.length} credentials changed in the project. Their new values will be used.`}
        </Text>
      )}

      {collapsible && !customising ? (
        <div className="flex items-center justify-between gap-bg-v2-md p-bg-v2-md bg-bg-secondary border border-border-secondary rounded-bg-v2-sm">
          <Text variant="text-xs" color="secondary">
            {summaryLine}
          </Text>
          <Button
            type="button"
            variant="link"
            size="small"
            label="Customise"
            disabled={disabled}
            onClick={() => setCustomising(true)}
          />
        </div>
      ) : (
        <div className="flex flex-col gap-bg-v2-md">
          {/* Open, the summary line stops being a lead-in and becomes the
              answer to "whose values are these" for the rows below it. */}
          <Text variant="text-xs" color="tertiary">
            {summaryLine}
          </Text>

          {rows}

          {ownCount > 0 && !disabled && (
            <div className="flex items-start">
              <Button
                type="button"
                variant="link"
                size="small"
                label="Use the project's values for all"
                disabled={disabled}
                // Resets every credential and stays open, so the fields
                // visibly change. Collapsing here hid the effect of the click
                // and took away the form the user was working in.
                onClick={revertAll}
              />
            </div>
          )}
        </div>
      )}

      {removed.length > 0 && (
        <Text variant="text-xs" color="tertiary" className="break-words">
          {removed.length === 1
            ? `'${removed[0].display_name}' is no longer part of the project and will be dropped from this environment.`
            : `${removed.length} credentials are no longer part of the project and will be dropped from this environment.`}
        </Text>
      )}
    </div>
  );
};

export default CredentialsSection;
